import { Controller, useFormContext } from "react-hook-form";
import { LGoTextField } from "@/ui/branded";
import { RadioGroup, RadioGroupItem } from "@/ui/primitives/radio-group";
import { Label } from "@/ui/primitives/label";
import type { BusinessProfileInput } from "@/domain/business/schema";

const UNLIMITED_HELP = "Zákazník smí slevu využít, kolikrát chce.";

const WINDOW_HELP =
  "Okno se posouvá s termínem: k termínu se počítají využití za tolik dní zpět, ne od pondělí nebo od prvního.";

/** Reads as one sentence rather than two fields, because the count and the
 *  window only mean anything together. */
export function DiscountUsageLimitField() {
  const { control, register, watch, formState } = useFormContext<BusinessProfileInput>();

  const isLimited = watch("hasDiscountLimit");
  const countError = formState.errors.discountUsesPerCustomer?.message;
  const daysError = formState.errors.discountUsesPeriodDays?.message;

  return (
    <div className="flex flex-col gap-3">
      <Controller
        control={control}
        name="hasDiscountLimit"
        render={({ field }) => (
          <RadioGroup
            value={field.value ? "limited" : "unlimited"}
            onValueChange={(choice) => field.onChange(choice === "limited")}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="unlimited" id="discount-unlimited" />
              <Label htmlFor="discount-unlimited" className="font-normal">
                Bez omezení
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="limited" id="discount-limited" />
              <Label htmlFor="discount-limited" className="font-normal">
                Omezit počet využití
              </Label>
            </div>
          </RadioGroup>
        )}
      />

      {isLimited && (
        <div className="flex flex-wrap items-center gap-2 pl-6 text-sm text-foreground">
          <span>Jeden zákazník smí využít vaši slevu</span>
          <LGoTextField
            type="number"
            min={1}
            step={1}
            aria-label="Počet využití slevy"
            error={countError}
            fieldClassName="w-28"
            iconRight="×"
            {...register("discountUsesPerCustomer", {
              setValueAs: (value) => (value === "" || value === null ? null : Number(value)),
            })}
          />
          <span>za posledních</span>
          <LGoTextField
            type="number"
            min={1}
            step={1}
            aria-label="Počet dní, za které se využití počítají"
            error={daysError}
            fieldClassName="w-32"
            iconRight="dní"
            {...register("discountUsesPeriodDays", {
              setValueAs: (value) => (value === "" || value === null ? null : Number(value)),
            })}
          />
        </div>
      )}

      <p className="text-xs text-muted-foreground">{isLimited ? WINDOW_HELP : UNLIMITED_HELP}</p>
    </div>
  );
}
